import { lazy, Suspense } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Box, CircularProgress } from '@mui/material';

import theme from './theme';
import MainLayout from './components/layout/MainLayout';
import { AuthProvider, useAuth } from './components/auth/AuthProvider';
import LoginPage from './components/auth/LoginButton';
import ErrorBoundary from './components/common/ErrorBoundary';
import RequirePermission from './components/auth/RequirePermission';
import { Permission } from './types/auth';
import './styles/signe-plus.css';

// Lazy loading des pages pour code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ReferentielCompetences = lazy(() => import('./pages/ReferentielCompetences'));
const CandidatesList = lazy(() => import('./pages/CandidatesList'));
const CandidateProfile = lazy(() => import('./pages/CandidateProfile'));
const JobsList = lazy(() => import('./pages/JobsList'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const NouveauBesoin = lazy(() => import('./pages/NouveauBesoin'));
const OfferEdit = lazy(() => import('./pages/OfferEdit'));
const JobsTurnoverList = lazy(() => import('./pages/JobsTurnoverListV2'));
const Profile = lazy(() => import('./pages/Profile'));
const CRMDashboard = lazy(() => import('./pages/CRMDashboard'));
const CRMOfferPipeline = lazy(() => import('./pages/CRMOfferPipeline'));
const OffersListV2 = lazy(() => import('./pages/OffersListV2'));
const CRMComptes = lazy(() => import('./pages/CRMComptes'));
const ClientsList = lazy(() => import('./pages/ClientsList'));
const FavoritesList = lazy(() => import('./pages/FavoritesList'));
const CompaniesList = lazy(() => import('./pages/CompaniesList'));
const CompanyDetail = lazy(() => import('./pages/CompanyDetail'));
const AIAdmin = lazy(() => import('./pages/AIAdmin'));
// Page PUBLIQUE (hors authentification) — landing de réponse candidat
const CandidateAvailabilityForm = lazy(() => import('./pages/CandidateAvailabilityForm'));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000,   // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Composant de chargement élégant
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
      flexDirection: 'column',
      gap: 2
    }}
  >
    <CircularProgress size={60} thickness={4} />
    <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
      Chargement...
    </Box>
  </Box>
);

// Composant qui gère l'authentification
function AuthenticatedApp() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <MainLayout>
      <ErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/candidates" element={<CandidatesList />} />
            <Route path="/candidates/:id" element={<CandidateProfile />} />
            <Route path="/jobs" element={<JobsList />} />
            <Route path="/jobs/:id" element={<JobDetail />} />
            <Route path="/clients" element={
              <RequirePermission permission={Permission.CLIENTS_VIEW} fallback={<Navigate to="/" replace />}>
                <ClientsList />
              </RequirePermission>
            } />
            <Route path="/offers" element={<Navigate to="/crm/offers" replace />} />
            {/* L'ancien formulaire en 6 étapes est masqué (13/10, décision d'Olivier) : ses
                liens et favoris mènent à la fiche « Nouveau besoin ». pages/OfferCreate.tsx est
                gardé le temps d'être sûr ; le remettre = rétablir la route. */}
            <Route path="/offers/new" element={<Navigate to="/offers/nouveau" replace />} />
            <Route path="/offers/nouveau" element={
              <RequirePermission permission={Permission.OFFERS_CREATE} fallback={<Navigate to="/" replace />}>
                <NouveauBesoin />
              </RequirePermission>
            } />
            <Route path="/offers/:offerId/edit" element={
              <RequirePermission permission={Permission.OFFERS_UPDATE} fallback={<Navigate to="/" replace />}>
                <OfferEdit />
              </RequirePermission>
            } />
            <Route path="/jobs-turnover" element={<JobsTurnoverList />} />
            <Route path="/admin" element={
              <RequirePermission permission={Permission.ADMIN_ES_MANAGEMENT} fallback={<Navigate to="/" replace />}>
                <AdminDashboard />
              </RequirePermission>
            } />
            <Route path="/profile" element={<Profile />} />
            <Route path="/crm" element={
              <RequirePermission permission={Permission.CRM_VIEW} fallback={<Navigate to="/" replace />}>
                <CRMDashboard />
              </RequirePermission>
            } />
            <Route path="/crm/offers" element={
              <RequirePermission permission={Permission.CRM_VIEW} fallback={<Navigate to="/" replace />}>
                <OffersListV2 />
              </RequirePermission>
            } />
            <Route path="/crm/comptes" element={
              <RequirePermission permission={Permission.CRM_VIEW} fallback={<Navigate to="/" replace />}>
                <CRMComptes />
              </RequirePermission>
            } />
            {/* Ancienne URL de test → canonicalise vers /crm/offers (désormais la V2) */}
            <Route path="/crm/offers-v2" element={<Navigate to="/crm/offers" replace />} />
            <Route path="/crm/my-offers" element={<Navigate to="/crm/offers?scope=mine" replace />} />
            <Route path="/crm/offers/:offerId/kanban" element={
              <RequirePermission permission={Permission.CRM_VIEW} fallback={<Navigate to="/" replace />}>
                <CRMOfferPipeline />
              </RequirePermission>
            } />
            <Route path="/favorites" element={<FavoritesList />} />
            <Route path="/companies" element={
              <RequirePermission permission={Permission.CLIENTS_VIEW} fallback={<Navigate to="/" replace />}>
                <CompaniesList />
              </RequirePermission>
            } />
            <Route path="/companies/:id" element={
              <RequirePermission permission={Permission.CLIENTS_VIEW} fallback={<Navigate to="/" replace />}>
                <CompanyDetail />
              </RequirePermission>
            } />
            <Route path="/referentiel-competences" element={
              <RequirePermission permission={Permission.REFERENTIEL_VIEW} fallback={<Navigate to="/" replace />}>
                <ReferentielCompetences />
              </RequirePermission>
            } />
            <Route path="/ai" element={
              <RequirePermission permission={Permission.ADMIN_ES_MANAGEMENT} fallback={<Navigate to="/" replace />}>
                <AIAdmin />
              </RequirePermission>
            } />
            {/* La file d'envois vit désormais dans /admin (onglet « File d'envois ») */}
            <Route path="/match-emails" element={<Navigate to="/admin" replace />} />
            <Route path="/auth/callback" element={<Dashboard />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </MainLayout>
  );
}

// Aiguillage racine : la landing candidat (/dispo/:token) est PUBLIQUE et ne doit
// jamais monter AuthProvider (pas de login Microsoft pour un candidat externe).
function RootRouter() {
  const { pathname } = useLocation();

  if (pathname.startsWith('/dispo/')) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/dispo/:token" element={<CandidateAvailabilityForm />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <BrowserRouter>
            <RootRouter />
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
